import {
  Box,
  Button,
  Divider,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Input,
  Select,
  Text,
  Textarea,
  VStack,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { createItem, updateItem } from "helpers/utils";
import { useState, useEffect } from "react";
import { Item, WithId } from "types";
import useUser from "./hooks/useUser";

const REQUIRED_FIELDS: Array<keyof Item> = ["name", "item_type"];

interface InventoryFormProps {
  onSave?: (item: WithId<Item>) => void;
  onClose?: () => void;
  item?: Partial<Item>;
}

export default function ItemForm({
  onSave,
  onClose,
  item: init,
}: InventoryFormProps) {
  const { onOpen, ...disclosure } = useDisclosure();
  const me = useUser();
  const toast = useToast();
  const [item, setItem] = useState<Partial<WithId<Item>>>({});

  // initialize edit item
  useEffect(() => {
    if (init) {
      setItem(init);
      onOpen();
    }
  }, [init, onClose, onOpen]);

  const handleChange = (name: keyof Item) => (e: any) => {
    const { value } = e.target;
    const numberTypes = ["reorder_level"];
    const val = numberTypes.includes(name) ? parseInt(value ?? 0) : value;
    setItem((prev) => ({ ...prev, [name]: val }));
  };

  const isValid = () => {
    const missing = REQUIRED_FIELDS.filter((field) => !item[field]);
    return missing.length === 0;
  };

  const handleSubmit = async () => {
    const verb = item.id ? "update" : "create";
    const verbPast = item.id ? "updated" : "created";

    if (!isValid()) {
      toast({
        title: "Error",
        description: "Please fill out all required fields",
        status: "error",
      });
      return;
    }

    try {
      const res = item.id
        ? await updateItem<Partial<Item>>(
            "Item",
            {
              ...item,
              company_id: me.company_id,
            },
            { fields: ["*", "inventory_items.*"] }
          )
        : await createItem<Partial<Item>>("Item", {
            ...item,
            company_id: me.company_id,
          });

      toast({
        title: "Success",
        description: `Item ${verbPast} successfully`,
        status: "success",
      });
      onSave?.(res as WithId<Item>);
      disclosure.onClose();
      handleClose();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error?.message ?? `Failed to ${verb} item`,
        status: "error",
      });
    }
  };

  const handleClose = () => {
    setItem({});
    onClose?.();
  };

  return (
    <>
      <Button onClick={onOpen} colorScheme="blue" variant="outline" size={"sm"}>
        New Item
      </Button>
      <Drawer
        {...disclosure}
        placement="right"
        size={"sm"}
        onCloseComplete={handleClose}
      >
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>{item.id ? "Edit" : "Create"} Inventory</DrawerHeader>
          <Divider />
          <DrawerBody>
            <VStack alignItems={"start"} py={2} spacing={4}>
              <Box w={"100%"}>
                <Text size={"sm"} mb={2}>
                  Item Name
                </Text>
                <Input
                  placeholder="Enter item name"
                  value={item.name ?? ""}
                  onChange={handleChange("name")}
                />
              </Box>
              <Box w={"100%"}>
                <Text size={"sm"} mb={2}>
                  Item Type
                </Text>
                <Select
                  placeholder="-- Select one --"
                  value={item.item_type ?? ""}
                  onChange={handleChange("item_type")}
                >
                  {["Consumable", "Asset"].map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </Select>
              </Box>
              <Box w={"100%"}>
                <Text size={"sm"} mb={2}>
                  Low Stock Level
                </Text>
                <Input
                  type="number"
                  placeholder="Reorder level"
                  value={item.reorder_level ?? ""}
                  onChange={handleChange("reorder_level")}
                />
              </Box>
              <Box w={"100%"}>
                <Text size={"sm"} mb={2}>
                  Description
                </Text>
                <Textarea
                  placeholder="Description"
                  value={item.description ?? ""}
                  onChange={handleChange("description")}
                />
              </Box>
            </VStack>
          </DrawerBody>
          <DrawerFooter>
            <Button variant="outline" mr={3} onClick={disclosure.onClose}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleSubmit}
              isDisabled={!isValid()}
            >
              Submit
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </>
  );
}
